import React, { useEffect, useState } from 'react';
import {
  Card,
  Select,
  Statistic,
  Row,
  Col,
  Spin,
  Button,
  Dropdown,
  Space,
  Progress,
  Empty,
  Tag,
} from 'antd';
import {
  Cog6ToothIcon,
  TrashIcon,
  ArrowPathIcon,
  ArrowsPointingOutIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  BellIcon,
  UserIcon,
  CurrencyDollarIcon,
  CursorArrowRaysIcon,
  Squares2X2Icon,
  RocketLaunchIcon,
  UserPlusIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import type { MenuProps } from 'antd';
import { useAppDispatch, useAppSelector } from 'store';
import { formatters } from 'utils/formatters';
import {
  fetchCardData,
  updateCard,
  removeCard,
  selectCardData,
  selectCardLoading,
} from 'store/slices/dashboard.slice';
import { selectApplications } from 'store/slices/applications.slice';
import { DashboardCardConfig } from 'types/dashboard.types';
import './DashboardCard.css';

type HeroIcon = React.ComponentType<React.SVGProps<SVGSVGElement>>;

const HIcon: React.FC<{ icon: HeroIcon; size?: number; style?: React.CSSProperties }> = ({
  icon: Icon,
  size = 18,
  style,
}) => <Icon style={{ width: size, height: size, ...style }} aria-hidden="true" />;

interface DashboardCardProps {
  config: DashboardCardConfig;
  onRemove?: () => void;
  isDragging?: boolean;
}

const DashboardCard: React.FC<DashboardCardProps> = ({
  config,
  onRemove,
  isDragging,
}) => {
  const dispatch = useAppDispatch();
  const applications = useAppSelector(selectApplications) || [];
  const cardData = useAppSelector(selectCardData(config.id));
  const isLoading = useAppSelector(selectCardLoading(config.id));
  const [isEditMode, setIsEditMode] = useState(!config.appId);

  useEffect(() => {
    if (config.appId) {
      loadData();
      const interval = setInterval(loadData,
        (config.refreshInterval || 30) * 1000
      );
      return () => clearInterval(interval);
    }
  }, [config.appId, config.type]);
  useEffect(() => {
    if (config.appId && isEditMode) {
      setIsEditMode(false);
    }
  }, [config.appId]);

  const loadData = () => {
    if (config.appId) {
      dispatch(fetchCardData({
        cardId: config.id,
        type: config.type,
        appId: config.appId,
      }));
    }
  };

  const handleAppChange = (appId: number) => {
    dispatch(updateCard({ ...config, appId }));
    setIsEditMode(false);
  };

  const getCardIcon = () => {
    const icons: Record<string, React.ReactNode> = {
      push_stats: <HIcon icon={BellIcon} />,
      user_stats: <HIcon icon={UserIcon} />,
      ctr: <HIcon icon={CursorArrowRaysIcon} />,
      app_stats: <HIcon icon={Squares2X2Icon} />,
      conversions: <HIcon icon={RocketLaunchIcon} />,
    };
    return icons[config.type] || <HIcon icon={Cog6ToothIcon} />;
  };

  const EmptyState: React.FC<{
    icon?: HeroIcon;
    description?: React.ReactNode;
    children?: React.ReactNode;
  }> = ({ icon: Icon = XCircleIcon, description, children }) => (
    <Empty
      image={<Icon style={{ width: 44, height: 44 }} />}
      imageStyle={{
        height: 'auto',
        display: 'flex',
        justifyContent: 'center',
        marginBottom: 12,
        color: 'var(--text-muted)',
        opacity: 0.5,
      }}
      description={description}
    >
      {children}
    </Empty>
  );

  const getCardTitle = () => {
    const titles: Record<string, string> = {
      push_stats: 'Push-уведомления',
      user_stats: 'Пользователи',
      ctr: 'CTR',
      app_stats: 'Статистика приложения',
      conversions: 'Конверсии',
    };
    return titles[config.type] || 'Статистика';
  };

  const renderContent = () => {
    if (isEditMode || !config.appId) {
      return (
        <div className="card-setup">
          <EmptyState
            description={isEditMode && config.appId ? 'Смените приложение' : 'Выберите приложение'}
          >
            <Select
              placeholder="Выберите приложение"
              style={{ width: '100%' }}
              onChange={handleAppChange}
              value={config.appId || undefined}
              options={applications.map(app => ({ label: app.name, value: app.id }))}
            />
            {isEditMode && config.appId && (
              <Button size="small" style={{ marginTop: 8 }} onClick={() => setIsEditMode(false)}>
                Отмена
              </Button>
            )}
          </EmptyState>
        </div>
      );
    }

    if (isLoading && !cardData) {
      return (
        <div className="card-loading">
          <Spin size="large" />
        </div>
      );
    }

    if (!cardData) {
      return <EmptyState description="Нет данных" />;
    }

    switch (config.type) {
      case 'push_stats':
        return (
          <div className="stats-content">
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Statistic
                  title="Отправлено"
                  value={cardData.pushStats?.sent || 0}
                  prefix={<HIcon icon={BellIcon} size={16} />}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="CTR"
                  value={cardData.pushStats?.ctr || 0}
                  suffix="%"
                  precision={2}
                  valueStyle={{
                    color: (cardData.pushStats?.ctr || 0) > 5 ? '#52c41a' : '#ff4d4f',
                  }}
                />
              </Col>
            </Row>
            <Progress
              percent={((cardData.pushStats?.delivered || 0) / (cardData.pushStats?.sent || 1)) * 100}
              size="small"
              format={percent => `Доставлено: ${percent?.toFixed(0)}%`}
            />
            {cardData.pushStats?.trend !== undefined && cardData.pushStats.trend !== 0 && (
              <div className="trend">
                {cardData.pushStats.trend > 0 ? (
                  <HIcon icon={ArrowUpIcon} size={14} style={{ color: '#52c41a' }} />
                ) : (
                  <HIcon icon={ArrowDownIcon} size={14} style={{ color: '#f5222d' }} />
                )}
                <span>{Math.abs(cardData.pushStats.trend).toFixed(1)}%</span>
              </div>
            )}
          </div>
        );

      case 'user_stats':
        return (
          <div className="stats-content">
            <Row gutter={[16, 16]}>
              <Col span={8}>
                <Statistic
                  title="Всего"
                  value={cardData.userStats?.total || 0}
                  valueStyle={{ fontSize: 20 }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Активных"
                  value={cardData.userStats?.active || 0}
                  valueStyle={{ fontSize: 20, color: '#52c41a' }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Новых"
                  value={cardData.userStats?.new || 0}
                  valueStyle={{ fontSize: 20, color: '#1890ff' }}
                />
              </Col>
            </Row>
          </div>
        );

      case 'ctr':
        return (
          <div className="stats-content">
            <Statistic
              title="Коэффициент кликов"
              value={cardData.ctrStats?.ctr || 0}
              suffix="%"
              precision={2}
              valueStyle={{
                color: (cardData.ctrStats?.ctr || 0) > 5 ? '#52c41a' : '#faad14',
              }}
            />
            <div style={{ marginTop: 16, fontSize: 12 }}>
              <div>Доставлено: {formatters.number(cardData.ctrStats?.delivered || 0)}</div>
              <div>Кликов: {formatters.number(cardData.ctrStats?.clicked || 0)}</div>
              <div>Среднее на пуш: {(cardData.ctrStats?.averagePerPush || 0).toFixed(1)}</div>
            </div>
          </div>
        );

      case 'app_stats':
        return (
          <div className="stats-content">
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 500 }}>{cardData.appStats?.name}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{cardData.appStats?.bundleId}</div>
            </div>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Statistic
                  title="Пользователей"
                  value={cardData.appStats?.totalUsers || 0}
                  valueStyle={{ fontSize: 20 }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Push-уведомлений"
                  value={cardData.appStats?.totalPushes || 0}
                  valueStyle={{ fontSize: 20 }}
                />
              </Col>
            </Row>
            <div style={{ marginTop: 16 }}>
              <Tag color={cardData.appStats?.isActive ? 'success' : 'default'}>
                {cardData.appStats?.isActive ? 'Активно' : 'Неактивно'}
              </Tag>
            </div>
          </div>
        );

      case 'conversions':
        return (
          <div className="stats-content">
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Statistic
                  title="Регистраций"
                  value={cardData.conversions?.registrations || 0}
                  prefix={<HIcon icon={UserPlusIcon} size={16} />}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Депозитов"
                  value={cardData.conversions?.deposits || 0}
                  prefix={<HIcon icon={CurrencyDollarIcon} size={16} />}
                />
              </Col>
            </Row>
            <div style={{ marginTop: 16 }}>
              <Progress
                percent={cardData.conversions?.conversionRate || 0}
                size="small"
                format={percent => `Конверсия: ${percent?.toFixed(1)}%`}
                strokeColor="#52c41a"
              />
            </div>
          </div>
        );

      default:
        return <EmptyState description="Тип карточки не поддерживается" />;
    }
  };

  const menuItems: MenuProps['items'] = [
    {
      key: 'edit',
      icon: <HIcon icon={Cog6ToothIcon} size={16} />,
      label: 'Настроить',
      onClick: () => {
        if (!isEditMode) {
          setIsEditMode(true);
        }
      },
    },
    {
      key: 'refresh',
      icon: <HIcon icon={ArrowPathIcon} size={16} />,
      label: 'Обновить',
      onClick: loadData,
      disabled: !config.appId || isEditMode,
    },
    {
      key: 'remove',
      icon: <HIcon icon={TrashIcon} size={16} />,
      label: 'Удалить',
      danger: true,
      onClick: () => {
        dispatch(removeCard(config.id));
        onRemove?.();
      },
    },
  ];

  return (
    <Card
      className={`dashboard-card card-type-${config.type} ${isDragging ? 'dragging' : ''}`}
      title={
        <div className="card-header">
          <Space>
            {getCardIcon()}
            <span>{getCardTitle()}</span>
          </Space>
          {config.appId && !isEditMode && (
            <span className="app-name">
              {applications.find(a => a.id === config.appId)?.name}
            </span>
          )}
        </div>
      }
      extra={
        <Space>
          <Button
            type="text"
            icon={<HIcon icon={ArrowsPointingOutIcon} size={16} />}
            className="drag-handle"
            size="small"
          />
          <Dropdown menu={{ items: menuItems }} trigger={['click']}>
            <Button type="text" icon={<HIcon icon={Cog6ToothIcon} size={16} />} size="small" />
          </Dropdown>
        </Space>
      }
      loading={isLoading && !isEditMode && !!config.appId}
    >
      {renderContent()}
    </Card>
  );
};

export default DashboardCard;